import { Container } from "@/components/layout/Container";
import { SEO } from "@/components/shared/SEO";
import { BlogCard } from "@/components/blog/BlogCard";
import { CategoryFilter } from "@/components/blog/CategoryFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Blog, BlogResponse } from "@shared/types";

export default function BlogList() {
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch blogs and categories
  const { data: blogsData, isLoading: blogsLoading, error: blogsError } = useQuery<BlogResponse>({
    queryKey: ['/api/blogs'],
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['/api/categories'],
  });

  // Get unique categories from blogs
  const availableCategories = useMemo(() => {
    if (!blogsData?.contents) return [];
    
    const categoryMap = new Map();
    blogsData.contents.forEach((blog: Blog) => {
      if (blog.category && !categoryMap.has(blog.category.id)) {
        categoryMap.set(blog.category.id, blog.category);
      }
    });
    
    return Array.from(categoryMap.values());
  }, [blogsData?.contents]);

  // Filter blogs based on category and search
  const filteredBlogs = useMemo(() => {
    if (!blogsData?.contents) return [];
    
    let filtered = blogsData.contents;

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((blog: Blog) => blog.category?.id === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((blog: Blog) =>
        blog.title?.toLowerCase().includes(query) ||
        (blog.content && blog.content.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [blogsData?.contents, selectedCategory, searchQuery]);

  if (blogsError) {
    return (
      <>
        <SEO
          title="Blog"
          description="技術ブログ記事の一覧です。"
          keywords="blog, tech blog, programming"
        />
        <Container className="py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center"
          >
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold mb-4">記事を読み込めませんでした</h1>
            <p className="text-muted-foreground">
              しばらく時間をおいてからもう一度お試しください。
            </p>
          </motion.div>
        </Container>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Blog"
        description="技術ブログ記事の一覧です。"
        keywords="blog, tech blog, programming"
      />
      <Container className="py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold tracking-tighter mb-4">Blog</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              技術に関する記事や開発で得た知見などを共有しています。
            </p>
          </div>

          {/* Search and filters */}
          <div className="space-y-6 mb-12">
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="記事を検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category filter */}
            {blogsLoading ? (
              <div className="flex flex-wrap gap-2 justify-center">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-16 rounded-full" />
                ))}
              </div>
            ) : availableCategories.length > 0 ? (
              <div className="flex justify-center">
                <CategoryFilter
                  categories={availableCategories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                />
              </div>
            ) : null}
          </div>

          {/* Blog posts */}
          {blogsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="h-48 w-full rounded-t-lg" />
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-2/3" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredBlogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBlogs.map((blog: Blog, index: number) => (
                <BlogCard key={blog.id} blog={blog} index={index} />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">記事が見つかりませんでした</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || selectedCategory 
                  ? "検索条件を変更してもう一度お試しください。"
                  : "まだ記事が投稿されていません。"
                }
              </p>
              {(searchQuery || selectedCategory) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory(undefined);
                  }}
                >
                  フィルターをクリア
                </Button>
              )}
            </motion.div>
          )}
        </motion.div>
      </Container>
    </>
  );
}